import { isEmpty } from 'lodash';
import { deviceType, isAndroid, isIOS, isMobile } from 'react-device-detect';

function sendAndroidPocapanMessage(message) {
  if (window.PocapanNativeBridge?.postMessage) {
    window.PocapanNativeBridge.postMessage(message);
    return true;
  }
  return false;
}

export const sendToNative = (message) => {
  if (window.webkit?.messageHandlers?.pocapanEvent) {
    window.webkit.messageHandlers.pocapanEvent.postMessage(message);
    return true;
  }
  if (window.webkit?.messageHandlers?.fanheartsEvent) {
    window.webkit.messageHandlers.fanheartsEvent.postMessage(message);
    return true;
  }
  return sendAndroidPocapanMessage(message);
};

export function isNativeSocialLoginBridgeAvailable() {
  if (typeof window === 'undefined') return false;
  if (window.webkit?.messageHandlers?.pocapanEvent) return true;
  if (window.webkit?.messageHandlers?.fanheartsEvent) return true;
  if (window.PocapanNativeBridge?.postMessage) return true;
  return false;
}

export const isNativeIOS = () => {
  return (
    window.webkit?.messageHandlers?.pocapanEvent ||
    window.webkit?.messageHandlers?.fanheartsEvent ||
    false
  );
};

export const isNativeAndroid = () => {
  return Boolean(
    window.Android?.isPocapanAndroidApp?.() || navigator.userAgent.includes('AndroidPocapan')
  );
};

class NativeMeta {
  constructor() {
    let version = localStorage.getItem('appVersion');
    if (isEmpty(version) && isNativeAndroid()) {
      const versionRegex = /AndroidPocapan (\d+\.\d+\.\d+)/;
      const match = navigator.userAgent.match(versionRegex);
      if (match) {
        version = match[1];
      }
    }
    this.appVersion = version ?? '';
  }

  // #region version
  setAppVersion = (version) => {
    try {
      if (!version) return;

      localStorage.setItem('appVersion', version);
      if (!isEmpty(this)) {
        this.appVersion = version;
      }
    } catch (_error) {
      // Nothing todo
    }
  };

  getShortAppVersion() {
    const regex = /^(?<v>\d+\.\d+\.\d+)/;
    const version = this.getAppVersion();
    const match = regex.exec(version);
    if (match) {
      const { v } = match.groups;
      return v;
    }
    return version;
  }

  getAppVersion() {
    return this.appVersion;
  }

  getAppVersionString() {
    if (isMobile) {
      const platform = isIOS ? 'iOS' : isAndroid ? 'Android' : 'etc';
      const app = isNativeIOS() ? '_app' : '';
      return `${deviceType}_${platform}${app}_${this.getAppVersion()}`;
    }
    return `desktop_${this.getAppVersion()}`;
  }
  // #endregion
}

const meta = new NativeMeta();

window.setAppVersion = meta.setAppVersion;

export function getNativeMeta() {
  return meta;
}

function readNativeMinVersionFloor() {
  const raw = import.meta.env.REACT_APP_NATIVE_MIN_VERSION_FLOOR;
  if (typeof raw !== 'string') return null;
  const v = raw.trim();
  if (!/^\d+\.\d+(\.\d+)?/.test(v)) return null;
  return v;
}

function readBooleanEnv(raw) {
  if (typeof raw !== 'string') return false;
  return ['1', 'true', 'yes', 'on'].includes(raw.trim().toLowerCase());
}

const nativeMinVersionFloor = readNativeMinVersionFloor();
const disableNativeMinVersionCheck = readBooleanEnv(
  import.meta.env.REACT_APP_DISABLE_NATIVE_MIN_VERSION_CHECK
);

const minimumAppVersionIOS = nativeMinVersionFloor ?? '1.1.7';
const minimumAppVersionAndroid = nativeMinVersionFloor ?? '1.1.9';

const minimumPushListeningVersionIOS = nativeMinVersionFloor ?? '1.1.9';
const minimumPushListeningVersionAndroid = nativeMinVersionFloor ?? '1.2.0';

const minimumSocialLoginVersionIOS = nativeMinVersionFloor ?? '1.2.0';
const minimumSocialLoginVersionAndroid = nativeMinVersionFloor ?? '1.2.1';

function isVersionAtLeast(currentVersion, minimumVersion) {
  if (!currentVersion) return false;
  const current = currentVersion.split('.').map(Number);
  const minimum = minimumVersion.split('.').map(Number);
  for (let i = 0; i < Math.max(current.length, minimum.length); i++) {
    const cv = current[i] || 0;
    const mv = minimum[i] || 0;
    if (cv > mv) return true;
    if (cv < mv) return false;
  }
  return true;
}

export function shouldShowSocialLoginMethods() {
  const isNativeApp = isNativeIOS() || isNativeAndroid();
  if (!isNativeApp) {
    return true;
  }
  const currentVersion = meta.getShortAppVersion();
  if (!currentVersion) return false;
  const minimumVersion = isNativeIOS()
    ? minimumSocialLoginVersionIOS
    : minimumSocialLoginVersionAndroid;
  return isVersionAtLeast(currentVersion, minimumVersion);
}

export function requireAppUpdate() {
  if (disableNativeMinVersionCheck) {
    return false;
  }

  const minimumVersion = isNativeIOS() ? minimumAppVersionIOS : minimumAppVersionAndroid;
  const currentVersion = meta.getShortAppVersion();

  if (!currentVersion) {
    return !nativeMinVersionFloor;
  }

  const segments1 = minimumVersion.split('.').map(Number);
  const segments2 = meta.getShortAppVersion().split('.').map(Number);

  for (let i = 0; i < Math.max(segments1.length, segments2.length); i++) {
    const segment1 = segments1[i] || 0;
    const segment2 = segments2[i] || 0;

    if (segment1 > segment2) {
      return true;
    }
    if (segment1 < segment2) {
      return false;
    }
  }
  // 버전이 같음
  return false;
}

const NATIVE_APP_SETTINGS_EVENT = 'native:appSettings';
const NATIVE_APP_SETTINGS_TIMEOUT_MS = 5000;

/**
 * 앱 브리지에서 locale, language 등 앱 설정을 가져옵니다.
 * - Android: window.Android.getAppSettings() 동기 호출
 * - iOS: sendToNative 후 native:appSettings 이벤트 수신
 * @returns {Promise<object | null>}
 */
export function getAppSettings() {
  if (isNativeAndroid() && window.Android?.getAppSettings) {
    try {
      const raw = window.Android.getAppSettings();
      if (typeof raw === 'string') {
        return Promise.resolve(JSON.parse(raw));
      }
      return Promise.resolve(raw ?? null);
    } catch (_error) {
      return Promise.resolve(null);
    }
  }

  if (isNativeIOS()) {
    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        cleanup();
        resolve(null);
      }, NATIVE_APP_SETTINGS_TIMEOUT_MS);

      const handler = (event) => {
        const detail = event.detail;
        cleanup();
        resolve(detail ?? null);
      };

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        window.removeEventListener(NATIVE_APP_SETTINGS_EVENT, handler);
      };

      window.addEventListener(NATIVE_APP_SETTINGS_EVENT, handler);
      sendToNative(JSON.stringify({ type: 'getAppSettings' }));
    });
  }

  return Promise.resolve(null);
}

export function isPushListeningEnabled() {
  const isNativeApp = isNativeIOS() || isNativeAndroid();
  if (!isNativeApp) {
    return false;
  }
  const currentVersion = meta.getShortAppVersion();
  if (!currentVersion) {
    return false;
  }
  const minimumVersion = isNativeIOS()
    ? minimumPushListeningVersionIOS
    : minimumPushListeningVersionAndroid;

  const current = currentVersion.split('.').map(Number);
  const minimum = minimumVersion.split('.').map(Number);

  for (let i = 0; i < Math.max(current.length, minimum.length); i++) {
    const cv = current[i] || 0;
    const mv = minimum[i] || 0;
    if (cv > mv) {
      return true;
    }
    if (cv < mv) {
      return false;
    }
  }
  // 버전이 같음
  return true;
}
